import { Box, Flex, VStack } from '@chakra-ui/react';
import { ChangeEvent, ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { B2bOptionalFieldLabel } from '../B2bOptionalFieldLabel';
import { B2bRegistrationSectionHeading } from '../B2bRegistrationSectionHeading/B2bRegistrationSectionHeading';
import { B2bYesNoToggle } from '../B2bYesNoToggle/B2bYesNoToggle';
import {
  B2B_REGISTRATION_CITY_FIELD_NAME,
  B2B_REGISTRATION_COMPANY_OR_ORGANIZATION_FIELD_NAME,
  B2B_REGISTRATION_EMAIL_FIELD_NAME,
  B2B_REGISTRATION_FULL_NAME_FIELD_NAME,
  B2B_REGISTRATION_PHONE_FIELD_NAME,
  B2B_REGISTRATION_PREVIOUS_EVENTS_FIELD_NAME,
  B2B_REGISTRATION_WEBSITE_OR_SOCIAL_MEDIA_FIELD_NAME,
  b2bRegistrationFieldLabelMessageId,
  b2bRegistrationFieldPlaceholderMessageId,
} from '../b2bRegistrationFormFields';
import type { B2bSelectOption } from '../FieldSelect/FieldSelect';
import { FieldPhone } from '../FieldPhone/FieldPhone';
import { FieldSelect } from '../FieldSelect/FieldSelect';
import { FieldText } from '../FieldText/FieldText';
import { FieldTextarea } from '../FieldTextarea/FieldTextarea';

export type B2bOrganizerDetailsFieldKey =
  | 'fullName'
  | 'companyOrOrganization'
  | 'phoneNumber'
  | 'email'
  | 'city'
  | 'websiteOrSocialMedia'
  | 'hasOrganizedBefore'
  | 'previousEvents';

export type B2bOrganizerDetailsSectionProps = {
  /** Options for the city `FieldSelect` (values are contract `city` strings). */
  cityOptions: readonly B2bSelectOption[];
  fullName: string;
  onFullNameChange: (value: string) => void;
  companyOrOrganization: string;
  onCompanyOrOrganizationChange: (value: string) => void;
  /** E.164 when valid; parent may use `''` until blur normalization runs. */
  phoneNormalizedValue: string;
  onPhoneNormalizedValueChange: (value: string | null) => void;
  email: string;
  onEmailChange: (value: string) => void;
  cityValue: string | undefined;
  onCityValueChange: (value: string | undefined) => void;
  websiteOrSocialMedia: string;
  onWebsiteOrSocialMediaChange: (value: string) => void;
  hasOrganizedBefore: boolean;
  onHasOrganizedBeforeChange: (value: boolean) => void;
  previousEvents: string;
  onPreviousEventsChange: (value: string) => void;
  errors?: Partial<Record<B2bOrganizerDetailsFieldKey, ReactNode>>;
  isDisabled?: boolean;
  /** Layout direction; matches page locale. */
  dir?: 'ltr' | 'rtl';
  lang?: string;
};

/**
 * **Organizer details** — one RTL block composing P2 field primitives (text, phone, select, text).
 * Wire from React Hook Form in P4 (`control` / `watch` / `setValue`); this ticket is props-only.
 */
export const B2bOrganizerDetailsSection = ({
  cityOptions,
  fullName,
  onFullNameChange,
  companyOrOrganization,
  onCompanyOrOrganizationChange,
  phoneNormalizedValue,
  onPhoneNormalizedValueChange,
  email,
  onEmailChange,
  cityValue,
  onCityValueChange,
  websiteOrSocialMedia,
  onWebsiteOrSocialMediaChange,
  hasOrganizedBefore,
  onHasOrganizedBeforeChange,
  previousEvents,
  onPreviousEventsChange,
  errors,
  isDisabled,
  dir = 'rtl',
  lang = 'ar',
}: B2bOrganizerDetailsSectionProps) => {
  const intl = useIntl();
  const fullNamePlaceholder = intl.formatMessage({
    id: b2bRegistrationFieldPlaceholderMessageId('fullName'),
    defaultMessage: 'Enter your full name',
  });
  const companyPlaceholder = intl.formatMessage({
    id: b2bRegistrationFieldPlaceholderMessageId('companyOrOrganization'),
    defaultMessage: 'Organization name',
  });
  const emailPlaceholder = intl.formatMessage({
    id: b2bRegistrationFieldPlaceholderMessageId('email'),
    defaultMessage: 'you@example.com',
  });
  const websitePlaceholder = intl.formatMessage({
    id: b2bRegistrationFieldPlaceholderMessageId('websiteOrSocialMedia'),
    defaultMessage: 'https://…',
  });
  const previousEventsPlaceholder = intl.formatMessage({
    id: b2bRegistrationFieldPlaceholderMessageId('previousEvents'),
    defaultMessage: 'Past events you were involved in',
  });

  const handleFullNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    onFullNameChange(e.target.value);
  };
  const handleCompanyChange = (e: ChangeEvent<HTMLInputElement>) => {
    onCompanyOrOrganizationChange(e.target.value);
  };
  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    onEmailChange(e.target.value);
  };
  const handleWebsiteChange = (e: ChangeEvent<HTMLInputElement>) => {
    onWebsiteOrSocialMediaChange(e.target.value);
  };
  const handlePreviousEventsChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onPreviousEventsChange(e.target.value);
  };

  return (
    <Box w='100%' dir={dir} lang={lang}>
      <VStack spacing={4} align='stretch' w='100%'>
        <B2bRegistrationSectionHeading dir={dir}>
          <FormattedMessage
            id='b2b.registration.section.organizerDetails.title'
            defaultMessage='Organizer information'
          />
        </B2bRegistrationSectionHeading>

        <Flex direction={{ base: 'column', lg: 'row' }} gap={4} w='full' align='stretch' dir={dir}>
          <VStack spacing={4} align='stretch' flex={{ lg: 1 }} minW={0}>
            <FieldText
              id={`b2b-field-${B2B_REGISTRATION_FULL_NAME_FIELD_NAME}`}
              name={B2B_REGISTRATION_FULL_NAME_FIELD_NAME}
              label={
                <FormattedMessage
                  id={b2bRegistrationFieldLabelMessageId('fullName')}
                  defaultMessage='Full name'
                />
              }
              value={fullName}
              onChange={handleFullNameChange}
              isRequired
              isDisabled={isDisabled}
              error={errors?.fullName}
              placeholder={fullNamePlaceholder}
              layoutDir={dir}
            />

            <FieldPhone
              id={`b2b-field-${B2B_REGISTRATION_PHONE_FIELD_NAME}`}
              name={B2B_REGISTRATION_PHONE_FIELD_NAME}
              dir={dir}
              label={
                <FormattedMessage
                  id='b2b.registration.field.phoneNumber.label'
                  defaultMessage='Phone number'
                />
              }
              normalizedValue={phoneNormalizedValue}
              onNormalizedValueChange={onPhoneNormalizedValueChange}
              isRequired
              isDisabled={isDisabled}
              error={errors?.phoneNumber}
            />

            <FieldSelect
              id={`b2b-field-${B2B_REGISTRATION_CITY_FIELD_NAME}`}
              name={B2B_REGISTRATION_CITY_FIELD_NAME}
              label={
                <FormattedMessage id='b2b.registration.field.city.label' defaultMessage='City' />
              }
              options={cityOptions}
              value={cityValue}
              onValueChange={onCityValueChange}
              isRequired
              isDisabled={isDisabled}
              error={errors?.city}
              requiredPlaceholder={
                <FormattedMessage
                  id='b2b.registration.field.selectPlaceholder'
                  defaultMessage='Choose a city'
                />
              }
              layoutDir={dir}
            />
          </VStack>

          <VStack spacing={4} align='stretch' flex={{ lg: 1 }} minW={0}>
            <FieldText
              id={`b2b-field-${B2B_REGISTRATION_COMPANY_OR_ORGANIZATION_FIELD_NAME}`}
              name={B2B_REGISTRATION_COMPANY_OR_ORGANIZATION_FIELD_NAME}
              label={
                <B2bOptionalFieldLabel>
                  <FormattedMessage
                    id={b2bRegistrationFieldLabelMessageId('companyOrOrganization')}
                    defaultMessage='Company or organization'
                  />
                </B2bOptionalFieldLabel>
              }
              value={companyOrOrganization}
              onChange={handleCompanyChange}
              isDisabled={isDisabled}
              error={errors?.companyOrOrganization}
              placeholder={companyPlaceholder}
              layoutDir={dir}
            />

            <FieldText
              id={`b2b-field-${B2B_REGISTRATION_EMAIL_FIELD_NAME}`}
              name={B2B_REGISTRATION_EMAIL_FIELD_NAME}
              type='email'
              label={
                <B2bOptionalFieldLabel>
                  <FormattedMessage
                    id={b2bRegistrationFieldLabelMessageId('email')}
                    defaultMessage='Email'
                  />
                </B2bOptionalFieldLabel>
              }
              value={email}
              onChange={handleEmailChange}
              isDisabled={isDisabled}
              error={errors?.email}
              placeholder={emailPlaceholder}
              layoutDir={dir}
            />

            <FieldText
              id={`b2b-field-${B2B_REGISTRATION_WEBSITE_OR_SOCIAL_MEDIA_FIELD_NAME}`}
              name={B2B_REGISTRATION_WEBSITE_OR_SOCIAL_MEDIA_FIELD_NAME}
              type='url'
              label={
                <B2bOptionalFieldLabel>
                  <FormattedMessage
                    id={b2bRegistrationFieldLabelMessageId('websiteOrSocialMedia')}
                    defaultMessage='Website or social media'
                  />
                </B2bOptionalFieldLabel>
              }
              value={websiteOrSocialMedia}
              onChange={handleWebsiteChange}
              isDisabled={isDisabled}
              error={errors?.websiteOrSocialMedia}
              placeholder={websitePlaceholder}
              layoutDir={dir}
            />
          </VStack>
        </Flex>

        <B2bYesNoToggle
          groupLabel={
            <FormattedMessage
              id='b2b.registration.field.hasOrganizedBefore.label'
              defaultMessage='I have organized or co-organized similar events before'
            />
          }
          yesLabel={<FormattedMessage id='b2b.registration.yesNo.yes' defaultMessage='Yes' />}
          noLabel={<FormattedMessage id='b2b.registration.yesNo.no' defaultMessage='No' />}
          value={hasOrganizedBefore}
          onChange={onHasOrganizedBeforeChange}
          isDisabled={isDisabled}
          error={errors?.hasOrganizedBefore}
          dir={dir}
        />

        {hasOrganizedBefore ? (
          <FieldTextarea
            id={`b2b-field-${B2B_REGISTRATION_PREVIOUS_EVENTS_FIELD_NAME}`}
            name={B2B_REGISTRATION_PREVIOUS_EVENTS_FIELD_NAME}
            label={
              <FormattedMessage
                id={b2bRegistrationFieldLabelMessageId('previousEvents')}
                defaultMessage='Previous events'
              />
            }
            value={previousEvents}
            onChange={handlePreviousEventsChange}
            isRequired
            isDisabled={isDisabled}
            error={errors?.previousEvents}
            placeholder={previousEventsPlaceholder}
            layoutDir={dir}
          />
        ) : null}
      </VStack>
    </Box>
  );
};
